import { useState } from 'react'
import { IsoField } from '../../models/IsoField'
import { FieldUtil } from '../../services/field-util'

export default function Iso8583BitmapParser() {
  const [rawMessage, setRawMessage] = useState('')
  const [fields, setFields] = useState<IsoField[]>(FieldUtil.getDefaultFields())
  const [error, setError] = useState('')
  const [showSelectedOnly, setShowSelectedOnly] = useState(false)

  const handleFieldToggle = (fieldId: number) => {
    const updated = fields.map((f) =>
      f.fieldId === fieldId ? { ...f, isSelected: !f.isSelected } : f,
    )
    setFields(updated)
    setRawMessage(FieldUtil.calculateBitmap(updated))
  }

  const handleParse = () => {
    setError('')
    try {
      const parsed = FieldUtil.parseBitmap(rawMessage, fields)
      setFields(parsed)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to parse bitmap')
    }
  }

  return (
    <div className="min-h-screen p-2">
      <h2 className="mb-6 text-left text-2xl font-bold text-gray-900">
        ISO 8583 Bitmap Parser
      </h2>

      <div className="space-y-2">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <input
            value={rawMessage}
            onChange={(e) => {
              setRawMessage(FieldUtil.sanitizeHexInput(e.target.value, 16))
            }}
            onPaste={(e) => {
              e.preventDefault()
              const pasted = e.clipboardData.getData('text')
              setRawMessage(FieldUtil.sanitizeHexInput(pasted, 16))
            }}
            placeholder="Paste raw ISO 8583 bitmap here..."
            className="flex-grow rounded-xl border border-gray-300 bg-white p-3 text-gray-900 outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <button
            onClick={handleParse}
            className="rounded-xl bg-indigo-600 px-6 py-2 font-semibold text-white shadow transition hover:bg-indigo-700"
          >
            Parse
          </button>

          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={showSelectedOnly}
              onChange={() => setShowSelectedOnly(!showSelectedOnly)}
              className="h-4 w-4 cursor-pointer accent-indigo-600"
            />
            <span>Show Selected Only</span>
          </label>
        </div>

        {error && <div className="mt-2 font-medium text-red-600">{error}</div>}

        <div className="mt-2 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden rounded-lg shadow-sm outline outline-1 outline-black/5">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-2 px-3 py-2 text-left text-sm font-semibold text-gray-900">
                        Select
                      </th>
                      <th className="w-2 px-3 py-2 text-left text-sm font-semibold text-gray-900">
                        Field
                      </th>
                      <th className="px-3 py-2 text-left text-sm font-semibold text-gray-900">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {(showSelectedOnly ? fields.filter((f) => f.isSelected) : fields).map(
                      (field) => (
                        <tr
                          key={field.fieldId}
                          className={field.isSelected ? 'bg-indigo-50' : ''}
                        >
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={field.isSelected}
                              onChange={() => handleFieldToggle(field.fieldId)}
                              className="h-4 w-4 cursor-pointer accent-indigo-600"
                            />
                          </td>
                          <td className="px-3 py-2 text-sm font-medium whitespace-nowrap text-gray-500">
                            {field.fieldNumber}
                          </td>
                          <td className="px-3 py-2 text-left text-sm whitespace-nowrap text-gray-500">
                            {field.description}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
